import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

const PURPOSE_ORDER = [
  'privacy_terms',
  'event_photo_upload',
  'event_face_grouping',
  'reference_face_match',
  'cross_event_memory',
  'notifications',
];

export default function PrivacySettings({ profileApi, onAccountDeleted }) {
  const [state, setState] = useState(null);
  const [checked, setChecked] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [deleteArmed, setDeleteArmed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await profileApi.getPrivacyConsents();
      setState(data);
      setChecked(Object.fromEntries(Object.keys(data.active || {}).map(p => [p, true])));
    } catch (err) {
      setMessage({ severity: 'error', text: err.message || 'Failed to load privacy settings.' });
    } finally {
      setLoading(false);
    }
  }, [profileApi]);

  useEffect(() => { load(); }, [load]);

  const purposes = state?.purposes || {};
  const changed = PURPOSE_ORDER.filter(p => Boolean(checked[p]) !== Boolean(state?.active?.[p]));

  async function save() {
    setSaving(true);
    setMessage(null);
    try {
      const toGrant = changed.filter(p => checked[p]);
      const toWithdraw = changed.filter(p => !checked[p]);
      let latest = state;
      if (toGrant.length) latest = await profileApi.grantPrivacyConsents(toGrant, null, 'profile');
      for (const purpose of toWithdraw) latest = await profileApi.withdrawPrivacyConsent(purpose);
      setState(latest);
      setChecked(Object.fromEntries(Object.keys(latest.active || {}).map(p => [p, true])));
      setMessage({ severity: 'success', text: 'Privacy choices saved.' });
    } catch (err) {
      setMessage({ severity: 'error', text: err.message || 'Failed to save privacy choices.' });
    } finally {
      setSaving(false);
    }
  }

  async function exportData() {
    setSaving(true);
    try {
      const data = await profileApi.exportPrivacyData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `circlemoments-data-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setMessage({ severity: 'success', text: 'Your data export is ready.' });
    } catch (err) {
      setMessage({ severity: 'error', text: err.message || 'Export failed.' });
    } finally {
      setSaving(false);
    }
  }

  async function deleteAccount() {
    if (!deleteArmed) {
      setDeleteArmed(true);
      return;
    }
    setSaving(true);
    try {
      await profileApi.deleteAccount();
      localStorage.removeItem('auth_token');
      onAccountDeleted?.();
      window.location.href = '/auth';
    } catch (err) {
      setMessage({ severity: 'error', text: err.message || 'Account deletion failed.' });
      setSaving(false);
    }
  }

  if (loading) return <CircularProgress size={22} />;

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="h6" fontWeight={700}>Privacy</Typography>
        <Typography variant="body2" color="text.secondary">
          Manage consent for photo storage, face grouping, reference selfie matching, cross-event memory, and account data rights.
        </Typography>
      </Box>

      {message && <Alert severity={message.severity}>{message.text}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          {PURPOSE_ORDER.map((purpose) => {
            const item = purposes[purpose];
            if (!item) return null;
            const active = Boolean(state?.active?.[purpose]);
            return (
              <Box key={purpose}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(checked[purpose])}
                      onChange={e => setChecked(c => ({ ...c, [purpose]: e.target.checked }))}
                    />
                  }
                  label={<Typography fontWeight={700}>{item.title}</Typography>}
                />
                <Typography variant="body2" color="text.secondary" sx={{ pl: 4 }}>
                  {item.description}
                  {active && state.active[purpose]?.granted_at ? ` Active since ${new Date(state.active[purpose].granted_at).toLocaleString()}.` : ''}
                </Typography>
              </Box>
            );
          })}
          <Button variant="contained" onClick={save} disabled={saving || changed.length === 0}>
            {saving ? 'Saving...' : 'Save Privacy Choices'}
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1" fontWeight={700}>Your Data</Typography>
          <Typography variant="body2" color="text.secondary">
            Export your active account, event, request, submission, memory, notification, and consent records.
          </Typography>
          <Button variant="outlined" onClick={exportData} disabled={saving}>Export My Data</Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'error.light' }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1" fontWeight={700} color="error">Delete Account</Typography>
          <Typography variant="body2" color="text.secondary">
            Deletes your account record and active storage owned by your account, including profile assets, owned event storage, submissions, and memory data where technically linked.
          </Typography>
          <Divider />
          <Button color="error" variant={deleteArmed ? 'contained' : 'outlined'} onClick={deleteAccount} disabled={saving}>
            {deleteArmed ? 'Confirm Delete Account' : 'Delete Account'}
          </Button>
        </Stack>
      </Paper>
    </Stack>
  );
}
